import { useEffect, useState, useCallback, useRef } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";

const WINDOWS = [
  { label: "All", value: 0 },
  { label: "Last 5 min", value: 300 },
  { label: "Last 1 hour", value: 3600 },
];

const shortEndpoint = (ep) => {
  // host/sap/bc/srt/scs/sap/querycustomerin1  ->  querycustomerin1
  const parts = String(ep || "").split("/").filter(Boolean);
  return parts[parts.length - 1] || ep;
};

const ms = (v) => (v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${Math.round(v)} ms`);

const latColor = (v) => (v >= 3000 ? "#dc2626" : v >= 1200 ? "#d97706" : "#16a34a");

export default function SapHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [win, setWin] = useState(0);
  const [auto, setAuto] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async (window_seconds) => {
    try {
      const r = await api.get(`/sap/health/metrics`, { params: { window_seconds: window_seconds ?? win } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load SAP health metrics");
    }
    setLoading(false);
  }, [win]);

  useEffect(() => { setLoading(true); load(win); }, [win, load]);

  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    if (auto) timer.current = setInterval(() => load(win), 5000);
    return () => timer.current && clearInterval(timer.current);
  }, [auto, win, load]);

  const reset = async () => {
    if (!window.confirm("Clear the recorded latency samples?")) return;
    try { await api.post("/sap/health/reset"); toast.success("Metrics reset"); load(win); }
    catch { toast.error("Reset failed"); }
  };

  const clearCaches = async () => {
    try { const r = await api.post("/sap/health/cache/clear"); toast.success(`Cleared ${r.data.cleared} cached lookups`); }
    catch { toast.error("Cache clear failed"); }
  };

  const cfg = data?.config || {};
  const o = data?.overall || {};

  const Chip = ({ children, tone = "muted" }) => (
    <span style={{
      fontSize: 11, padding: "3px 9px", borderRadius: 20, fontWeight: 700,
      background: tone === "green" ? "#dcfce7" : tone === "blue" ? "#e0f2fe" : "#f1f5f9",
      color: tone === "green" ? "#166534" : tone === "blue" ? "#075985" : "#475569",
    }}>{children}</span>
  );

  const Kpi = ({ label, value, color }) => (
    <div style={{ border: "1px solid var(--omh-border)", borderRadius: 10, padding: "14px 16px", minWidth: 130 }}>
      <div style={{ fontSize: 11, color: "var(--omh-text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, color: color || "var(--omh-text)" }}>{value}</div>
    </div>
  );

  return (
    <Layout>
      <div data-testid="sap-health-page" style={{ maxWidth: 1100, display: "grid", gap: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>SAP Health</h1>
          <p style={{ color: "var(--omh-text-muted)", fontSize: 13, marginTop: 6 }}>
            Live latency and error rate for every SAP ByDesign call. All SAP requests share one
            pooled, keep-alive connection (no per-call TLS handshake) and are capped to a safe
            concurrency limit so bursts can't stampede the tenant.
          </p>
        </div>

        {/* Config + controls */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <Chip tone="green">Connection reuse: ON</Chip>
          <Chip tone="blue">Max concurrency: {cfg.max_concurrency ?? "—"}</Chip>
          <Chip>Pool: {cfg.pool_keepalive_connections ?? "—"} keep-alive / {cfg.pool_max_connections ?? "—"} max</Chip>
          <Chip>Timeout: {cfg.timeout_seconds ?? "—"}s</Chip>
          <div style={{ flex: 1 }} />
          <select data-testid="sap-health-window" value={win} onChange={(e) => setWin(Number(e.target.value))}
            style={{ padding: "7px 10px", borderRadius: 8, border: "1px solid var(--omh-border)", fontSize: 13 }}>
            {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input data-testid="sap-health-auto" type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
            Auto-refresh
          </label>
          <button data-testid="sap-health-refresh-btn" onClick={() => load(win)}
            style={{ padding: "8px 16px", background: "#0EA5E9", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
            Refresh
          </button>
          <button data-testid="sap-health-clearcache-btn" onClick={clearCaches}
            style={{ padding: "8px 14px", background: "#fff", color: "#111827", border: "1px solid var(--omh-border)", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
            Clear caches
          </button>
          <button data-testid="sap-health-reset-btn" onClick={reset}
            style={{ padding: "8px 14px", background: "#fff", color: "#dc2626", border: "1px solid var(--omh-border)", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
            Reset
          </button>
        </div>

        {loading ? <div style={{ padding: 16 }}>Loading…</div> : (
          <>
            {/* Overall KPIs */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }} data-testid="sap-health-kpis">
              <Kpi label="SAP calls" value={o.count ?? 0} />
              <Kpi label="Error rate" value={`${((o.error_rate || 0) * 100).toFixed(1)}%`} color={o.errors ? "#dc2626" : "#16a34a"} />
              <Kpi label="p50" value={ms(o.p50_ms || 0)} color={latColor(o.p50_ms || 0)} />
              <Kpi label="p95" value={ms(o.p95_ms || 0)} color={latColor(o.p95_ms || 0)} />
              <Kpi label="p99" value={ms(o.p99_ms || 0)} color={latColor(o.p99_ms || 0)} />
              <Kpi label="Max" value={ms(o.max_ms || 0)} color={latColor(o.max_ms || 0)} />
            </div>

            {(o.count ?? 0) === 0 && (
              <div style={{ background: "#f8fafc", border: "1px solid var(--omh-border)", padding: 14, borderRadius: 8, fontSize: 13, color: "var(--omh-text-muted)" }}>
                No SAP calls recorded yet in this window. Use a SAP screen (Push, Outbound, pickers)
                and the timings will appear here.
              </div>
            )}

            {/* Per-service breakdown */}
            <div style={{ border: "1px solid var(--omh-border)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", fontWeight: 700, fontSize: 14, background: "var(--omh-bg-muted, #f8fafc)" }}>
                By SAP service (slowest first)
              </div>
              <table data-testid="sap-health-endpoints" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--omh-bg-muted, #f8fafc)", textAlign: "left" }}>
                    {["Service", "Calls", "Errors", "p50", "p95", "p99", "Max"].map((h) =>
                      <th key={h} style={{ padding: "9px 12px", fontWeight: 700 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(data?.endpoints || []).length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: 16, color: "var(--omh-text-muted)" }}>No data.</td></tr>
                  ) : (data.endpoints).map((e) => (
                    <tr key={e.endpoint} style={{ borderTop: "1px solid var(--omh-border)" }} title={e.endpoint}>
                      <td style={{ padding: "9px 12px", fontWeight: 600, fontFamily: "Inter, sans-serif" }}>{shortEndpoint(e.endpoint)}</td>
                      <td style={{ padding: "9px 12px" }}>{e.count}</td>
                      <td style={{ padding: "9px 12px", color: e.errors ? "#dc2626" : "inherit" }}>{e.errors}</td>
                      <td style={{ padding: "9px 12px", color: latColor(e.p50_ms) }}>{ms(e.p50_ms)}</td>
                      <td style={{ padding: "9px 12px", color: latColor(e.p95_ms) }}>{ms(e.p95_ms)}</td>
                      <td style={{ padding: "9px 12px", color: latColor(e.p99_ms) }}>{ms(e.p99_ms)}</td>
                      <td style={{ padding: "9px 12px", color: latColor(e.max_ms) }}>{ms(e.max_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Slowest recent calls */}
            <div style={{ border: "1px solid var(--omh-border)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", fontWeight: 700, fontSize: 14, background: "var(--omh-bg-muted, #f8fafc)" }}>
                Slowest recent calls
              </div>
              <table data-testid="sap-health-slowest" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--omh-bg-muted, #f8fafc)", textAlign: "left" }}>
                    {["When", "Method", "Service", "Duration", "Status"].map((h) =>
                      <th key={h} style={{ padding: "9px 12px", fontWeight: 700 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(data?.slowest || []).length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: 16, color: "var(--omh-text-muted)" }}>No data.</td></tr>
                  ) : data.slowest.map((s, i) => (
                    <tr key={i} style={{ borderTop: "1px solid var(--omh-border)" }} title={s.endpoint}>
                      <td style={{ padding: "9px 12px", color: "var(--omh-text-muted)", fontSize: 12 }}>{(s.ts || "").slice(11, 19)}</td>
                      <td style={{ padding: "9px 12px" }}>{s.method}</td>
                      <td style={{ padding: "9px 12px", fontFamily: "Inter, sans-serif" }}>{shortEndpoint(s.endpoint)}</td>
                      <td style={{ padding: "9px 12px", fontWeight: 700, color: latColor(s.ms) }}>{ms(s.ms)}</td>
                      <td style={{ padding: "9px 12px", color: s.error ? "#dc2626" : "inherit" }}>{s.error || s.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
